import { useEffect, useSyncExternalStore } from 'react';

/*
 * Light / dark theme. The choice is per device (localStorage); « auto » follows the system.
 * `public/theme-init.js` applies the same rule before the first paint (no flash) — keep them equal.
 * The workshop tablet has its own dark palette and suspends the theme while mounted.
 */

export type ThemePref = 'light' | 'dark' | 'system';
export type Theme = 'light' | 'dark';

export const THEME_PREFS: readonly ThemePref[] = ['light', 'dark', 'system'];

const KEY = 'cf.theme';
/** Browser chrome colour (PWA status bar), per resolved theme. */
const CHROME: Record<Theme, string> = { light: '#b45309', dark: '#111418' };

const media = () => window.matchMedia('(prefers-color-scheme: dark)');

const readPref = (): ThemePref => {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
};

let pref: ThemePref = readPref();
let suspended = 0;
const listeners = new Set<() => void>();

const resolve = (p: ThemePref): Theme =>
  p === 'system' ? (media().matches ? 'dark' : 'light') : p;

function apply() {
  const theme = suspended > 0 ? 'light' : resolve(pref);
  const root = document.documentElement;
  if (root.dataset.theme !== theme) root.dataset.theme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', CHROME[theme]);
}

function emit() {
  apply();
  for (const l of listeners) l();
}

let snapshot = { pref, theme: resolve(pref) };
const getSnapshot = () => {
  const theme = resolve(pref);
  if (snapshot.pref !== pref || snapshot.theme !== theme) snapshot = { pref, theme };
  return snapshot;
};

function subscribe(listener: () => void) {
  listeners.add(listener);
  const mq = media();
  mq.addEventListener('change', emit);
  return () => {
    listeners.delete(listener);
    mq.removeEventListener('change', emit);
  };
}

/** Where the reveal starts (the centre of the switch that was pressed). */
export interface RevealOrigin {
  x: number;
  y: number;
}

function setPref(next: ThemePref, origin?: RevealOrigin) {
  if (next === pref) return;
  try {
    if (next === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, next);
  } catch {
    /* private mode: the choice lasts for this tab */
  }
  const before = resolve(pref);
  const commit = () => {
    pref = next;
    emit();
  };
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!origin || reduced || before === resolve(next) || !document.startViewTransition) {
    commit();
    return;
  }
  const transition = document.startViewTransition(commit);
  const { x, y } = origin;
  const r = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y));
  void transition.ready.then(() => {
    document.documentElement.animate(
      { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${r}px at ${x}px ${y}px)`] },
      {
        duration: 520,
        easing: 'cubic-bezier(0.32, 0.72, 0, 1)',
        pseudoElement: '::view-transition-new(root)',
      },
    );
  });
}

export function useTheme() {
  const s = useSyncExternalStore(subscribe, getSnapshot);
  return { ...s, setPref };
}

/** Pins the light tokens while mounted (the workshop tablet draws its own dark palette). */
export function useSuspendTheme() {
  useEffect(() => {
    suspended += 1;
    apply();
    return () => {
      suspended -= 1;
      apply();
    };
  }, []);
}

/** Centre of the element that triggered the switch. */
export const originOf = (el: Element): RevealOrigin => {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
};

/** Safety net when `theme-init.js` did not run (e.g. offline before it was cached). */
export function initTheme() {
  if (!location.pathname.startsWith('/operator')) apply();
}
